import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { V1ComputeCorrelationMatrixResponse } from '@blk/portfolio-security-server/analytics/correlation';
import { CURRENCY } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN, APPLICATION_NAME_TOKEN, ApplicationName } from '@ddt-web/config';
import { InjectConfig, PcConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig, PasRcResolverService } from '@ddt-web/data-service';
import { ApplicationNames } from '@ddt-web/domain';
import { PasApiService, PasPortfolioPosition } from '@ddt-web/pas';
import { BpasEngagement, BpasEngagementsStoreService } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import * as get from 'lodash/get';
import { switchMap, take, withLatestFrom } from 'rxjs/operators';

import { PortfolioPositionsUtil } from '../../utils';

@Injectable()
export class PasCorrelationMatrixDataLoaderService {
	@InjectConfig(PcConfigKey.CORRELATION_MATRIX_FUNDS_COUNT) private readonly correlationMatrixFundsCount: number = 40;

	private readonly CORRELATION_MATRIX = 'correlation-matrix';
	private readonly CORRELATION_MATRIX_OVERALL_ID = 'overall';

	constructor(
		private pasApiService: PasApiService,
		private bpasEngagementsStoreService: BpasEngagementsStoreService,
		private pasRcResolver: PasRcResolverService,
		private activatedRoute: ActivatedRoute,
		private storeService: StoreService,
		@Inject(APPLICATION_NAME_TOKEN) protected applicationName: ApplicationName,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) { }

	public buildAssetIdDataAndPopulateStore(filteredIds: string[], portfoliosToLoad: PasPortfolioPosition[]): void {
		if (portfoliosToLoad?.length > 1) {
			this.buildTopWeightedHoldings(portfoliosToLoad, this.CORRELATION_MATRIX_OVERALL_ID);
		}
		portfoliosToLoad.forEach((portfolio, i) => {
			this.buildTopWeightedHoldings([portfolio], filteredIds[i]);
		});
	}


	public populateStoresForCorrelationMatrix(portfolioIds: string[], assetIdToFundName: Map<string, string>): void {
		let correlationMatrix$;
		const assetIds: string[] = Array.from(assetIdToFundName.keys());

		if (this.applicationName === ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION) {
			correlationMatrix$ = this.bpasEngagementsStoreService.selectCurrentEngagement$().
				pipe(
					take(1),
					withLatestFrom(this.pasRcResolver.getRcForUserType(this.CORRELATION_MATRIX)),
					switchMap(([engagement, rc]: [BpasEngagement, string]) => {
						const currencyCode = get(engagement, 'currency', CURRENCY.USD);
						return this.pasApiService.fetchCorrelationMatrix({ assetIds, currencyCode, rc });
					})
				);
		} else {
			correlationMatrix$ = this.activatedRoute.queryParams.
				pipe(
					take(1),
					withLatestFrom(this.pasRcResolver.getRcForUserType(this.CORRELATION_MATRIX)),
					switchMap(([qp, rc]: [any, string]) => {
						const currencyCode = qp['currency'] ?? CURRENCY.EUR;
						return this.pasApiService.fetchCorrelationMatrix({ assetIds, currencyCode, rc });
					})
				);
		}

		correlationMatrix$.
			pipe(
				take(1)
			).
			subscribe((result: V1ComputeCorrelationMatrixResponse) => {
				const matrixToStore: V1ComputeCorrelationMatrixResponse[] = this.replaceAssetIdWithFundName(result, assetIdToFundName);
				this.loadCorrelationMatrixData(portfolioIds, matrixToStore);
			});
	}

	private loadCorrelationMatrixData(portfolioIds: string[], data: V1ComputeCorrelationMatrixResponse[]): void {
		const portfolioIdToCorrelationMatrix = portfolioIds.map(id => {
			return { id, matrix: data };
		});

		this.storeService.upsertMany(this.storeConfig.correlationMatrix, portfolioIdToCorrelationMatrix);
	}

	private replaceAssetIdWithFundName(apiResponse: V1ComputeCorrelationMatrixResponse, assetIdToFundName: Map<string, string>): V1ComputeCorrelationMatrixResponse[] {
		const matrixRows = apiResponse.matrixRows || [];
		const assetIds: string[] = apiResponse.assetIds || [];

		// TODO wkennerl: remove when tested with more data
		const fundNames = assetIds.map((assetId: string) => assetIdToFundName.get(assetId) || assetId);
		return [{
			matrixRows,
			assetIds: fundNames
		}];
	}

	private buildTopWeightedHoldings(portfoliosToLoad: PasPortfolioPosition[], portfolioId: string): void {
		const topWeightedHoldings: Map<string, number> = PortfolioPositionsUtil.getTopNWeightedAssetIds(portfoliosToLoad, this.correlationMatrixFundsCount);
		const sortedAssetIdToAssetName: Map<string, string> = PortfolioPositionsUtil.mapSortedAssetIdToAssetName(portfoliosToLoad, topWeightedHoldings);
		this.populateStoresForCorrelationMatrix([portfolioId], sortedAssetIdToAssetName);
	}
}
